import React, { useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { useSelector } from 'react-redux';
import { selectActiveIssues } from '../../../modules/issues/stores/slice';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { doGetIssues } from '../../../modules/issues/stores/thunk';

export function ActiveIssues() {
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const activeIssues = useSelector((state) => selectActiveIssues(state));
  //const pagination = useSelector(selectActiveIssuesPagination);

  const fetchIssues = useCallback(
    async (status: string, page?: number) => {
      await dispatch(
        doGetIssues({
          status,
          page,
          take: 10,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchIssues('active');
    };

    fetchInitialData();
  }, [fetchIssues]);

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>Active Issues</CardTitle>
        <CardDescription>Your most recently updated active issues</CardDescription>
      </CardHeader>
      <CardContent>
        {activeIssues.length === 0 ? (
          <p className="text-center text-gray-500">No active issues</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Order ID</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="text-right">Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeIssues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500 pt-12">
                    No active issues now, enjoy your day!
                  </TableCell>
                </TableRow>
              ) : (
                activeIssues
                  .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()) // Sort by latest
                  .slice(0, 10) // Show only 10 latest issues
                  .map((issue) => (
                    <TableRow
                      className="hover:cursor-pointer"
                      key={issue.issueId}
                      onClick={(e) => {
                        const directTo = `/orders/${issue.orderId2}/issue/${issue.issueId}`;
                        // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
                        if (e.ctrlKey || e.metaKey) {
                          window.open(directTo, '_blank'); // Open in new tab
                        } else {
                          history.push(directTo); // Navigate in the same tab
                        }
                      }}
                    >
                      <TableCell className="font-medium">{issue.orderId2}</TableCell>
                      <TableCell>{formatIssueReason(issue.reason)}</TableCell>
                      <TableCell>{issue?.creatorUserName || 'Unknown'}</TableCell>
                      <TableCell className="text-right">{formatLastUpdated(issue.updatedAt)}</TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// Format Time Since Last Updated
export const formatLastUpdated = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hours ago`;
  return `${Math.floor(diffMinutes / 1440)} days ago`;
};

// Format Issue Reason for Display
const formatIssueReason = (reason: string): string => {
  return reason
    ?.split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
